import React from "react";
import { Attention, Button, DialogNew, Typography } from "@optimizely/axiom";
import { useForm } from "react-hook-form";

export const VENDOR_NAME_MAX_LENGTH = 32;
export const VENDOR_NAME_PATTERN = /^[a-z][a-z0-9._]*$/;
export const VENDOR_NAME_PATTERN_MESSAGE =
    "Must start with a lowercase letter and contain only lowercase letters, numbers, dots, and underscores.";

type VendorNameModalProps = {
    mode: "set" | "edit";
    initialName?: string;
    loading?: boolean;
    error?: string | null;
    onSave: (vendorName: string) => void;
    onCancel: () => void;
};

interface VendorNameFormValues {
    vendorName: string;
}

export const VendorNameModal = ({
    mode,
    initialName = "",
    loading = false,
    error,
    onSave,
    onCancel
}: VendorNameModalProps) => {
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isValid }
    } = useForm<VendorNameFormValues>({
        mode: "onChange",
        defaultValues: { vendorName: initialName }
    });

    const isEdit = mode === "edit";
    const currentLength = (watch("vendorName") || "").length;

    const onSubmit = handleSubmit(({ vendorName }) => onSave(vendorName.trim()));

    return (
        <DialogNew
            data-cy="vendor-name-modal"
            title={isEdit ? "Edit vendor name" : "Set vendor name"}
            hasCloseButton
            hasOverlay
            onClose={onCancel}
            footerButtonList={[
                <Button key={0} data-cy="vendor-name-cancel" isDisabled={loading} style="plain" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button
                    key={1}
                    data-cy="vendor-name-save"
                    isDisabled={loading || !isValid}
                    isLoading={loading}
                    loadingText="Save"
                    style="highlight" //eslint-disable-line react/style-prop-object
                    onClick={onSubmit}
                >
                    Save
                </Button>
            ]}
        >
            <div className="push--bottom">
                {error && (
                    <Attention type="bad-news" className="push--bottom">
                        {error}
                    </Attention>
                )}
                {isEdit && (
                    <Attention type="warning" className="push--bottom" data-testid="vendor-name-edit-warning">
                        Changing the vendor name can break published apps that rely on the current name.
                    </Attention>
                )}
                <Typography type="body" tag="p">
                    The vendor name identifies this organization in the App Directory. It must be unique across all
                    organizations.
                </Typography>
                <form onSubmit={onSubmit}>
                    <input
                        aria-label="Vendor name"
                        data-testid="vendor-name-input"
                        aria-describedby={errors.vendorName ? "vendor-name-error" : undefined}
                        aria-invalid={errors.vendorName ? true : undefined}
                        className="oui-text-input"
                        id="vendor-name"
                        placeholder="e.g. acme.analytics"
                        disabled={loading}
                        type="text"
                        {...register("vendorName", {
                            setValueAs: (value: string) => (value ?? "").trim(),
                            required: { value: true, message: "Vendor name is required" },
                            maxLength: {
                                value: VENDOR_NAME_MAX_LENGTH,
                                message: `Vendor name must be ${VENDOR_NAME_MAX_LENGTH} characters or fewer.`
                            },
                            pattern: {
                                value: VENDOR_NAME_PATTERN,
                                message: VENDOR_NAME_PATTERN_MESSAGE
                            }
                        })}
                    />
                    <div className="flex flex-justified--between">
                        {errors.vendorName ? (
                            <div className="oui-form-bad-news" id="vendor-name-error">
                                <span className="oui-form-note">{errors.vendorName.message}</span>
                            </div>
                        ) : (
                            <span />
                        )}
                        <span className="muted micro" data-testid="vendor-name-character-count" aria-live="polite">
                            {currentLength}/{VENDOR_NAME_MAX_LENGTH}
                        </span>
                    </div>
                </form>
            </div>
        </DialogNew>
    );
};

VendorNameModal.displayName = "VendorNameModal";
